import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { trackPurchase } from '../hooks/useGoogleAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { refreshCart } = useCart();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      navigate('/');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        refreshCart();
        
        // Track GA4 purchase event for Stripe payment
        // Retrieve stored cart items from sessionStorage
        const storedItems = sessionStorage.getItem('ga4_pending_purchase_items');
        const storedValue = sessionStorage.getItem('ga4_pending_purchase_value');
        
        let ga4Items = [];
        let purchaseValue = 0;
        
        if (storedItems) {
          try {
            ga4Items = JSON.parse(storedItems);
          } catch (e) {
            console.warn('[GA4] Could not parse stored items');
          }
        }
        
        if (storedValue) {
          purchaseValue = parseFloat(storedValue) || 0;
        }
        
        // Use Stripe session data if available
        if (response.data.amount_total) {
          purchaseValue = response.data.amount_total / 100; // Convert from cents
        }
        
        trackPurchase({
          transaction_id: sessionId,
          value: purchaseValue,
        }, ga4Items);
        
        // Clean up sessionStorage
        sessionStorage.removeItem('ga4_pending_purchase_items');
        sessionStorage.removeItem('ga4_pending_purchase_value');
        
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setStatus('processing');
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      setStatus('error');
      toast.error('Error al verificar el estado del pago');
    }
  };

  const formatPrice = (amount) => {
    // Amount comes in cents, convert to MXN
    const price = amount / 100;
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price) + ' MXN';
  };

  if (status === 'loading' || status === 'processing') {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="checkout-processing">
        <div className="text-center px-6">
          <div className="w-16 h-16 border-2 border-[#BA7B7C] border-t-transparent rounded-full animate-spin mx-auto mb-8"></div>
          <h1 
            className="text-3xl font-light text-[#1C1917] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Procesando tu pago...
          </h1>
          <p className="text-[#57534E]">Por favor espera un momento</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="checkout-success">
        <div className="text-center px-6 max-w-lg">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 
            className="text-4xl md:text-5xl font-light text-[#1C1917] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            ¡Gracias por tu compra!
          </h1>
          
          <p className="text-lg text-[#57534E] mb-8">
            Tu pedido ha sido procesado exitosamente. Recibirás un correo de confirmación pronto.
          </p>

          {paymentInfo && (
            <div className="bg-white rounded-2xl p-6 mb-8 text-left shadow-soft">
              <div className="flex items-center space-x-3 mb-4">
                <Package className="w-5 h-5 text-[#BA7B7C]" />
                <span className="font-medium text-[#1C1917]">Detalles del pedido</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#57534E]">Total pagado:</span>
                  <span className="text-[#1C1917] font-medium">
                    {formatPrice(paymentInfo.amount_total)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#57534E]">Estado:</span>
                  <span className="text-green-600 font-medium">Pagado</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop" className="btn-primary inline-flex items-center justify-center space-x-2">
              <span>Seguir comprando</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error or timeout state
  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="checkout-error">
      <div className="text-center px-6 max-w-lg">
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-8">
          <Package className="w-10 h-10 text-yellow-600" />
        </div>
        
        <h1 
          className="text-3xl font-light text-[#1C1917] mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {status === 'timeout' ? 'Verificación en proceso' : 'Algo salió mal'}
        </h1>
        
        <p className="text-[#57534E] mb-8">
          {status === 'timeout' 
            ? 'No pudimos confirmar tu pago inmediatamente. Por favor revisa tu correo para la confirmación.'
            : 'Hubo un problema al verificar tu pago. Por favor contacta a soporte si el cargo fue realizado.'
          }
        </p>

        <Link to="/" className="btn-primary">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
